import { inject, Injectable } from '@angular/core';
import { AUTH_SESSION } from '@shared/di/auth-session.token';
import { EnAuthOptionsOperator } from '../enums/auth-options-operator.enum';
import { hasSuperAdminWildcard, matchAuthOptions } from '@shared/utils/auth-match.utils';

@Injectable({ providedIn: 'root' })
export class PermissionService {
  private readonly permissionNames = inject(AUTH_SESSION).permissionNames;

  /**
   * Access gates read the session signal. A computed that calls these re-runs
   * when layout's readPermissions() lands, including SUPER_ADMIN `*`.
   */
  hasPermission(permission: string): boolean {
    // Same permissive gate as AuthService.hasPermissions: a missing name is
    // "no restriction", so a dynamic caller can pass '' without locking the UI.
    if (!permission) return true;
    return this.matches([permission], EnAuthOptionsOperator.AND);
  }

  hasAnyPermission(permissions: string[]): boolean {
    return this.matches(permissions, EnAuthOptionsOperator.OR);
  }

  hasAllPermissions(permissions: string[]): boolean {
    return this.matches(permissions, EnAuthOptionsOperator.AND);
  }

  /**
   * AuthOptions gate that reads the store signal. An empty id is unrestricted,
   * matching AuthService.hasPermissions / hasExactPermissions. `exact` skips
   * the SUPER_ADMIN wildcard.
   */
  allows(
    id: string | readonly string[] | null | undefined,
    operator: EnAuthOptionsOperator,
    exact = false,
  ): boolean {
    if (!id) return true;
    const required = typeof id === 'string' ? [id] : [...id];
    if (exact) {
      return matchAuthOptions(required, this.permissionNames(), operator);
    }
    return this.matches(required, operator);
  }

  // For context/label decisions — checks literal assigned permissions, ignores wildcard
  hasExactPermission(permission: string): boolean {
    return this.permissionNames().includes(permission);
  }

  hasAnyExactPermission(permissions: string[]): boolean {
    return permissions.some(p => this.permissionNames().includes(p));
  }

  private matches(permissions: string[], operator: EnAuthOptionsOperator): boolean {
    const names = this.permissionNames();
    if (hasSuperAdminWildcard(names)) {
      return true;
    }
    return matchAuthOptions(permissions, names, operator);
  }
}
